import { useGetInvestmentsSummary } from "hooks/investments";
import { useEffect, useMemo, useRef, useState } from "react";
import "features/companies/CompaniesTable/components/CompaniesSummary/CompaniesSummary.scss";

const BASE_CURRENCY = "PLN";

const InvestmentsSummary = () => {
  const { data: summary } = useGetInvestmentsSummary();

  const [selectedCurrency, setSelectedCurrency] =
    useState<string>(BASE_CURRENCY);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const availableCurrencies = useMemo(() => {
    if (!summary) {
      return [BASE_CURRENCY];
    }

    const keys = Object.keys(summary).filter(
      (key) => key !== "combined" && key !== BASE_CURRENCY,
    );

    const preferredOrder = ["EUR", "USD"];
    const orderedPreferred = preferredOrder.filter((c) => keys.includes(c));
    const otherCurrencies = keys
      .filter((c) => !preferredOrder.includes(c))
      .sort();

    return [BASE_CURRENCY, ...orderedPreferred, ...otherCurrencies];
  }, [summary]);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollState();
  }, [availableCurrencies]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "right" ? 80 : -80, behavior: "smooth" });
  };

  const currentTotalValue = useMemo(() => {
    if (!summary) return 0;

    const currencySummary = summary[
      selectedCurrency as keyof typeof summary
    ] as { totalValue?: number } | undefined;

    if (!currencySummary) return 0;

    return currencySummary.totalValue ?? 0;
  }, [summary, selectedCurrency]);

  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const handleCurrencyClick = (currency: string) => {
    setSelectedCurrency(currency);
    const button = buttonRefs.current[currency];
    if (button) {
      button.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  };

  const currencyLabel = useMemo(() => {
    if (selectedCurrency === BASE_CURRENCY) return "zł";
    if (selectedCurrency === "EUR") return "€";
    if (selectedCurrency === "USD") return "$";
    return selectedCurrency;
  }, [selectedCurrency]);

  useEffect(() => {
    if (!availableCurrencies.includes(selectedCurrency)) {
      setSelectedCurrency(BASE_CURRENCY);
    }
  }, [availableCurrencies, selectedCurrency]);

  return (
    <div className="summary">
      <div className="summary__container">
        <div className="summary__header">
          <h3>Portfel</h3>
          {availableCurrencies.length > 1 && (
            <div className="summary__currencies-wrapper">
              {canScrollLeft && (
                <button
                  type="button"
                  className="summary__scroll-btn summary__scroll-btn--left"
                  onClick={() => scroll("left")}
                  aria-label="Przewiń w lewo"
                >
                  ‹
                </button>
              )}
              <div
                ref={scrollRef}
                className="summary__currencies"
                onScroll={updateScrollState}
              >
                {availableCurrencies.map((currency) => (
                  <button
                    key={currency}
                    ref={(el) => {
                      buttonRefs.current[currency] = el;
                    }}
                    type="button"
                    className={
                      currency === selectedCurrency
                        ? "summary__currency-tab summary__currency-tab--active"
                        : "summary__currency-tab"
                    }
                    onClick={() => handleCurrencyClick(currency)}
                  >
                    {currency}
                  </button>
                ))}
              </div>
              {canScrollRight && (
                <button
                  type="button"
                  className="summary__scroll-btn summary__scroll-btn--right"
                  onClick={() => scroll("right")}
                  aria-label="Przewiń w prawo"
                >
                  ›
                </button>
              )}
            </div>
          )}
        </div>
        <p>
          {currentTotalValue.toLocaleString("pl-PL", {
            maximumFractionDigits: 2,
          })}{" "}
          <span className="summary__currency">{currencyLabel}</span>
        </p>
      </div>
      <div className="summary__container">
        <h3>Ilość zakupionych aktywów</h3>
        <p>{summary?.combined?.totalTypesCount ?? 0}</p>
      </div>
    </div>
  );
};

export default InvestmentsSummary;
